// Spinner — the indeterminate loading indicator, pure.
//
// Rebuilt from foundations v1 against the design vision (.claude/research/design/06).
// A Spinner has no colour of its own to spend: it is drawn in `currentColor`
// so it inherits the tone of whatever mounts it — neutral fg on a label,
// the accent inside a primary button, the agent's identity in a chat turn.
// This keeps the accent-discipline (§1) intact: the spinner never *decides*
// to be accent, it only echoes its context.
//
// Pure/props-driven: `size` picks the glyph scale; `label` is the accessible
// name announced by the `role="status"` live region. `label` is a caller-
// resolved Lingui string; when omitted we fall back to a localized "Loading"
// via the `t` macro (deps arrive through the Lingui context, no I/O here).
//
// Two sizes, a discriminated `SpinnerSize` union — no free-form number, so
// the glyph always lands on the token icon scale (16 / 20).

import { useLingui } from "@lingui/react/macro";
import "./Spinner.css";

type SpinnerSize = "sm" | "md";

export type SpinnerProps = {
    readonly size?: SpinnerSize;
    // aria-label for the status region. Caller passes a Lingui-resolved
    // string; omitted → a localized "Loading" default.
    readonly label?: string;
    // Optional layout hook (positioning/margins in the composing context);
    // never used to restyle the glyph itself.
    readonly className?: string;
};

export const Spinner = ({ size = "md", label, className }: SpinnerProps) => {
    const { t } = useLingui();

    // When label is provided, render it visibly alongside the spinner
    if (label) {
        return (
            <span
                className={`bilda-spinner-with-label${
                    className ? ` ${className}` : ""
                }`}
            >
                <span className={`bilda-spinner bilda-spinner--${size}`} />
                <span className="bilda-spinner-label">{label}</span>
            </span>
        );
    }

    // Without a label, render the spinner with a screen-reader fallback
    return (
        <span
            role="status"
            aria-label={t`Loading`}
            className={`bilda-spinner bilda-spinner--${size}`}
        />
    );
};
